import {
  batchCreateBasicField,
  batchCreateEvent,
  batchCreateRelationship,
  createFieldValue,
  createStandardizedFieldValue,
  getEventsConnectedToRelationships,
  getNestedOrFloatingElementById,
  getNextUuid,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'

import { editEventType } from './eventTypes'
import { editEventRelationship, ensureRelPeopleAreInSameRecord } from './eventRelationships'
import { getFieldIdAndValue, getOrCreateSuperIdFromFormState, removeFieldOrEvent } from './eventFieldUtils'

export const createEvent = ({
  values,
  selectedPerson,
  otherPerson,
  newEvent,
  attribution,
  types,
  selectedPersonId,
  addedEventId,
  eventTypeFieldId,
}) => {
  let dispatches = []
  const eventType = types?.events?.find((ev) => ev.eventType === values[addedEventId])
  const eventTypeLabel = eventType?.displayLabel
  const eventTypeFieldValue = createFieldValue(eventTypeLabel, attribution, eventType?.uri)
  const createDate = values.dateFields.length > 0
  const createPlace = values.placeFields.length > 0
  const handleRelationshipEvent = eventType.elementTypes.includes('RELATIONSHIP')
  let parentPersonOrRelationshipId = selectedPersonId

  if (handleRelationshipEvent) {
    parentPersonOrRelationshipId = getNextUuid()

    const relationshipDispatches = batchCreateRelationship({
      partitionKey: selectedPerson.partitionKey,
      person1ID: selectedPerson.id,
      person2ID: otherPerson?.id,
      person1PartitionKey: selectedPerson.partitionKey,
      person2PartitionKey: otherPerson?.partitionKey,
      relType: eventType.relType,
      id: parentPersonOrRelationshipId,
    })

    dispatches = [...dispatches, ...relationshipDispatches]
  }

  const eventDispatches = batchCreateEvent({
    partitionKey: selectedPerson.partitionKey,
    eventType: eventType?.eventType,
    personId: parentPersonOrRelationshipId,
    id: addedEventId,
    eventTypeFieldValue,
    createDate,
    createPlace,
    eventTypeId: eventTypeFieldId,
    personIdAsRelationshipId: handleRelationshipEvent,
  })

  const dateId = eventDispatches?.find(
    (action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === 'DATE'
  )?.value.id

  const placeId = eventDispatches?.find(
    (action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === 'PLACE'
  )?.value.id

  if (createDate) newEvent.dateId = dateId
  if (createPlace) newEvent.placeId = placeId
  dispatches = dispatches.concat(eventDispatches)

  return dispatches
}

// Helper function to handle field processing
const processField = ({
  field,
  key,
  value,
  el,
  selectedPerson,
  removedFieldsKeys,
  formState,
  attribution,
  eventTypeFieldId,
  types,
  slsImages,
  parentRelationship,
  otherPerson,
  movingPersonToOtherRecord,
  relationshipPerson,
  newEvent,
  eventElements,
  addedEventId,
}) => {
  let fieldDispatches = []

  if (el) {
    if (removedFieldsKeys.includes(key)) {
      fieldDispatches = removeFieldOrEvent({ key, el })
    } else if (el?.elementType === 'RELATIONSHIP') {
      fieldDispatches = editEventRelationship({
        slsImages,
        parentRelationship,
        selectedPerson,
        otherPerson,
        movingPersonToOtherRecord,
        relationshipPerson,
        el,
      })
    } else if (
      el?.elementType === 'EVENT' &&
      (formState.dirtyFields[key] || !el?.subElements?.find((se) => se.fieldType === 'EVENT_TYPE'))
    ) {
      fieldDispatches = editEventType({ attribution, el, value, eventTypeFieldId, types })
    } else if (formState.dirtyFields[key]) {
      const { id, value: fieldValue } = getFieldIdAndValue({ el, value, attribution })
      fieldDispatches = [{ type: 'ADD_FV', value: { partitionKey: el.partitionKey, id, fieldValue } }]
    }
  } else if (addedEventId !== key) {
    const { fieldType } = field
    const fieldValue = value?.standard
      ? createStandardizedFieldValue(value, fieldType, attribution)
      : createFieldValue(typeof value === 'string' ? value : value?.originalText || '', attribution)

    const {
      superId,
      superType,
      dispatches: superDispatches,
    } = getOrCreateSuperIdFromFormState({
      field,
      newEvent,
      eventElements,
      selectedPerson,
    })

    const newBasicFieldDispatches = batchCreateBasicField({
      partitionKey: selectedPerson.partitionKey,
      superId,
      fieldType,
      fieldValue,
      superElementType: superType,
      id: key,
    })

    fieldDispatches = [...superDispatches, ...newBasicFieldDispatches]
  }

  return fieldDispatches
}

// Helper function to initialize event data
const initializeEventData = (selectedPerson, supportEventsFromRelationships) => {
  const eventsFromRelationships = supportEventsFromRelationships
    ? getEventsConnectedToRelationships(selectedPerson)
    : []
  const personalEvents = selectedPerson?.subElements?.filter((se) => se.elementType === 'EVENT') || []
  return [...eventsFromRelationships, ...personalEvents]
}

// New helper to prepare initial event data and configuration
const prepareEventConfig = (selectedPerson, values, slsImages, supportEventsFromRelationships, appName) => {
  const eventElements = initializeEventData(selectedPerson, supportEventsFromRelationships)
  const eventTypeFieldId = values.eventTypeField.id
  const addedEventId = values.id
  const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: appName }
  const isNewEvent = values?.newEvent
  const newEvent = isNewEvent ? {} : null
  const otherPerson = getNestedOrFloatingElementById(slsImages, values?.relationshipPerson?.key)

  return {
    eventElements,
    eventTypeFieldId,
    addedEventId,
    attribution,
    isNewEvent,
    newEvent,
    otherPerson,
  }
}

// New helper to prepare field data
const prepareFieldData = (values) => {
  const dataFields = [
    ...values.dateFields,
    ...values.placeFields,
    ...values.otherFields,
    ...values.removedFields,
    { ...values.eventTypeField, id: values.id },
    values.parentRelationship,
  ].filter(Boolean)

  const removedFieldsKeys = values.removedFields.map((removedField) => removedField.id)

  return { dataFields, removedFieldsKeys }
}

// New helper to process relationship dispatches
const processRelationshipDispatches = (eventType, otherPerson, params) => {
  if (eventType?.elementTypes?.includes('RELATIONSHIP') && otherPerson) {
    return ensureRelPeopleAreInSameRecord({
      images: params.slsImages,
      selectedPerson: params.selectedPerson,
      otherPerson,
      movingPersonToOtherRecord: params.movingPersonToOtherRecord,
      keepThisRelationshipId: params.addedEventId,
    })
  }
  return []
}

export const createEventDispatches = (
  selectedPerson,
  values,
  types,
  formState,
  slsImages,
  supportEventsFromRelationships = true,
  appName = ''
) => {
  const { eventElements, eventTypeFieldId, addedEventId, attribution, isNewEvent, newEvent, otherPerson } =
    prepareEventConfig(selectedPerson, values, slsImages, supportEventsFromRelationships, appName)

  let dispatches = isNewEvent
    ? createEvent({
        newEvent,
        selectedPersonId: selectedPerson?.id,
        addedEventId,
        eventTypeFieldId,
        attribution,
        types,
        values,
        selectedPerson,
        otherPerson,
      })
    : []

  const { dataFields, removedFieldsKeys } = prepareFieldData(values)

  dataFields.forEach((field) => {
    const fieldDispatches = processField({
      field,
      key: field.id,
      value: values[field.id],
      el: getNestedOrFloatingElementById([selectedPerson], field.id),
      selectedPerson,
      removedFieldsKeys,
      formState,
      attribution,
      eventTypeFieldId,
      types,
      slsImages,
      parentRelationship: values.parentRelationship,
      otherPerson,
      movingPersonToOtherRecord: values.movingPersonToOtherRecord,
      relationshipPerson: values.relationshipPerson,
      newEvent,
      eventElements,
      addedEventId,
    })
    dispatches = [...dispatches, ...fieldDispatches]
  })

  dispatches = [
    ...dispatches,
    ...processRelationshipDispatches(values.eventType, otherPerson, {
      slsImages,
      selectedPerson,
      movingPersonToOtherRecord: values.movingPersonToOtherRecord,
      addedEventId,
    }),
  ]

  if (isNewEvent && selectedPerson?.recordId) {
    dispatches.push({
      type: 'SET_RECORD_PERSON_IDS',
      value: selectedPerson.recordId,
    })
  }

  return dispatches
}
